import { Alert, Box, CircularProgress, Paper, Typography } from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";
import { useEffect } from "react";
import {
  type NotificationShadowComparisonFilterInput,
  useNotificationShadowComparisonSummaryQuery,
} from "../../generated/graphql";
import { orderedSummaryCounts } from "./notification-shadow-status";

const styles = {
  container: { display: "flex", flexWrap: "wrap", gap: 2, mb: 1 },
  countCard: { p: 2, minWidth: 140 },
  loading: { display: "flex", alignItems: "center", gap: 1, p: 2 },
  caption: { mt: 1, maxWidth: 640 },
} satisfies Record<string, SxProps<Theme>>;

type NotificationShadowSummaryPanelProps = {
  readonly filter: NotificationShadowComparisonFilterInput;
  /** Bumped by the page's refresh control; 0 means "no manual refresh yet". */
  readonly refreshKey: number;
};

/**
 * At-a-glance counts of unresolved (PENDING) and divergent (MISMATCH/ORPHAN)
 * shadow-comparison rows for the current filter. A MATCH is never shown here:
 * matching rows self-prune at write time (deleted, only a metric kept), so
 * the count for a fully healthy shadow run is zero across the board.
 */
const NotificationShadowSummaryPanel = ({
  filter,
  refreshKey,
}: NotificationShadowSummaryPanelProps) => {
  const { data, loading, error, refetch } =
    useNotificationShadowComparisonSummaryQuery({ variables: { filter } });

  useEffect(() => {
    if (refreshKey === 0) return;
    void refetch();
  }, [refreshKey, refetch]);

  if (error !== undefined) {
    return (
      <Alert severity="error" sx={styles.caption}>
        Failed to load summary counts: {error.message}
      </Alert>
    );
  }

  if (loading && data === undefined) {
    return (
      <Box sx={styles.loading}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">
          Loading summary…
        </Typography>
      </Box>
    );
  }

  const counts = orderedSummaryCounts(
    data?.notificationShadowComparisonSummary ?? [],
  );

  return (
    <Box>
      <Box sx={styles.container}>
        {counts.map((entry) => (
          <Paper key={entry.status} variant="outlined" sx={styles.countCard}>
            <Typography variant="h4">{entry.count}</Typography>
            <Typography variant="body2" color="text.secondary">
              {entry.status}
            </Typography>
          </Paper>
        ))}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={styles.caption}>
        Matched deliveries are pruned; rows here are unresolved (PENDING) or
        divergent (MISMATCH/ORPHAN).
      </Typography>
    </Box>
  );
};

export default NotificationShadowSummaryPanel;
